/**
 * Round 218A Phase 8D-2 (2026-02-05) — Insufficient Business Credits
 * modal.
 *
 * Opens on:
 *   • customer clicks "Add credits" in the header pill
 *   • generation call returns HTTP 402 with
 *     error="insufficient_business_credits"
 *   • Settings section's Buy Credits click
 *
 * Customer-facing contract (locked):
 *   • Never shows how many credits the current asset requires.
 *   • Never shows the internal shortage math or the balance delta.
 *   • Never shows provider costs or gross margins.
 *   • Never mutates the customer's saved project.
 *   • On successful checkout return, the header pill refreshes and a
 *     "Continue" action resumes the intended generation. The parent
 *     is responsible for the actual resume — this modal just carries
 *     the intent forward via `saveResumeIntent` in the hook.
 *
 * Preview safety:
 *   • If the backend's /billing/credits response reports
 *     `packs_configured=false` (STRIPE_PRICE_CREDIT_PACK_* env vars
 *     blank), we render a Preview-only configuration message instead
 *     of broken checkout buttons.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Coins, CheckCircle } from '@phosphor-icons/react';
import { businessCreditsAPI } from '@/utils/api';
import useBusinessCredits from '@/hooks/useBusinessCredits';

const InsufficientCreditsModal = ({
  open,
  onClose,
  title = 'More Business Credits needed',
  message,
  resumeIntent = null,
  onContinueAfterPurchase,
}) => {
  const { balance, loading, refresh } = useBusinessCredits({ auto: open });
  const [purchasing, setPurchasing] = useState(null); // pack key while a checkout is opening

  if (!open) return null;

  const packs = balance?.packs || [];
  const packsConfigured = Boolean(balance?.packs_configured);
  const isUnlimited = Boolean(balance?.is_unlimited);

  const handlePurchase = async (packKey) => {
    if (!packsConfigured) return;
    setPurchasing(packKey);
    try {
      // Persist the resume intent to sessionStorage BEFORE redirecting
      // so the parent can pick it up in the success handler.
      if (resumeIntent) {
        try {
          window.sessionStorage.setItem(
            'paygbot.pending_generation_intent',
            JSON.stringify({
              ...resumeIntent,
              saved_at: new Date().toISOString(),
            }),
          );
        } catch {
          /* no-op */
        }
      }
      const { data } = await businessCreditsAPI.purchasePack(packKey);
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      toast.error('Could not open checkout. Please try again.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : 'Could not open checkout right now. Please try again in a moment.';
      toast.error(msg);
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-slate-950/80 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="insufficient-credits-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="insufficient-credits-title"
    >
      <div
        className={[
          'w-full sm:max-w-lg bg-slate-900 border border-slate-800',
          'rounded-t-2xl sm:rounded-2xl shadow-2xl',
          'max-h-[92vh] overflow-y-auto',
          'pb-[max(env(safe-area-inset-bottom),1rem)]',
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-emerald-500/15 p-2">
              <Coins size={20} weight="fill" className="text-emerald-400" />
            </div>
            <div>
              <h2
                id="insufficient-credits-title"
                className="text-lg font-semibold text-white"
                data-testid="insufficient-credits-title"
              >
                {title}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {message
                  || "You've used all of your Business Credits. Purchase more to continue generating professional business assets. Your saved project is safe — nothing has been lost."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 rounded-lg p-2 text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="insufficient-credits-close"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3">
          {isUnlimited && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              Your account has unlimited Business Credits. No purchase needed.
            </div>
          )}

          {!isUnlimited && !packsConfigured && (
            <div
              className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"
              data-testid="packs-preview-only"
            >
              Business Credit packs are being configured for this environment.
              Please try again shortly, or contact support if this persists.
            </div>
          )}

          {!isUnlimited && packsConfigured && (
            <div className="space-y-2" data-testid="credit-packs-list">
              {packs.map((pack) => (
                <button
                  key={pack.key}
                  type="button"
                  onClick={() => handlePurchase(pack.key)}
                  disabled={purchasing !== null || !pack.purchase_enabled}
                  className={[
                    'w-full flex items-center justify-between',
                    'rounded-xl border border-slate-700 bg-slate-800/60',
                    'px-4 py-3 text-left transition-colors',
                    'hover:border-emerald-500/50 hover:bg-slate-800',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  ].join(' ')}
                  data-testid={`credit-pack-${pack.key}`}
                >
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {pack.credits.toLocaleString('en-US')} Business Credits
                    </div>
                    <div className="text-xs text-slate-400">One-time purchase</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-bold text-white tabular-nums">
                      ${pack.price_usd_cents / 100}
                    </div>
                    {purchasing === pack.key && (
                      <div
                        className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {resumeIntent && (
            <div
              className="rounded-xl border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-400"
              data-testid="resume-intent-note"
            >
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle size={14} weight="fill" className="text-emerald-400" />
                <span className="font-medium">Your project is saved.</span>
              </div>
              <p className="mt-1 text-slate-400">
                After purchase, you&rsquo;ll return here and can Continue without
                rebuilding or re-approving anything.
              </p>
            </div>
          )}

          {/* Manual Continue action — visible after a purchase returns
              (the parent controls when this fires; it's optional in
              other flows). */}
          {onContinueAfterPurchase && (
            <button
              type="button"
              onClick={async () => {
                await refresh();
                onContinueAfterPurchase();
              }}
              disabled={loading}
              className="w-full rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
              data-testid="insufficient-credits-continue-btn"
            >
              Continue generating
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsufficientCreditsModal;
